// O tipo `Comando` junta os comandos de todos os agregados, e `executarComando`
// despacha pro executor certo. É o que o Store do app (src/app/store.ts) e o
// servidor (server/) usam — mesmo código.
//
// A forma na rede é sempre um `Comando` inteiro (`{ agregado, cmd }`): o app
// hospedado manda esse objeto pro `POST /api/comando`, que chama
// `validarComando` e depois `executarComando`. Cada agregado novo entra como
// mais um `case` aqui e em `validarComando` — a rota não muda.

import type { Snapshot } from "../domain/snapshot.ts";
import type { Modulo } from "../domain/schema.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import { comoObjeto } from "./_forma.ts";
import {
  executarComandoLote,
  validarComandoLote,
  type ComandoLote,
  type ContextoComando,
} from "./lotes.ts";
import {
  executarComandoDevolucao,
  validarComandoDevolucao,
  type ComandoDevolucao,
} from "./devolucao.ts";
import {
  executarComandoBacklogInicial,
  validarComandoBacklogInicial,
  type ComandoBacklogInicial,
} from "./backlog-inicial.ts";
import {
  executarComandoPerda,
  validarComandoPerda,
  type ComandoPerda,
} from "./perda.ts";
import {
  executarComandoFatura,
  validarComandoFatura,
  type ComandoFatura,
} from "./fatura.ts";
import {
  executarComandoCategoria,
  validarComandoCategoria,
  type ComandoCategoria,
} from "./categoria.ts";
import {
  executarComandoTipoPeca,
  validarComandoTipoPeca,
  type ComandoTipoPeca,
} from "./tipo-peca.ts";
import {
  executarComandoLista,
  validarComandoLista,
  type ComandoLista,
} from "./lista.ts";
import {
  executarComandoFuncionario,
  validarComandoFuncionario,
  type ComandoFuncionario,
} from "./funcionario.ts";
import {
  executarComandoInsumo,
  validarComandoInsumo,
  type ComandoInsumo,
} from "./insumo.ts";
import {
  executarComandoConfig,
  validarComandoConfig,
  type ComandoConfig,
} from "./config.ts";
import {
  executarComandoPreco,
  validarComandoPreco,
  type ComandoPreco,
} from "./preco.ts";
import {
  executarComandoCliente,
  validarComandoCliente,
  type ComandoCliente,
} from "./cliente.ts";
import {
  executarComandoResponsavel,
  executarComandoRetirada,
  validarComandoResponsavel,
  validarComandoRetirada,
  type ComandoResponsavel,
  type ComandoRetirada,
} from "./retirada.ts";
import {
  executarComandoConta,
  validarComandoConta,
  type ComandoConta,
} from "./conta.ts";
import {
  executarComandoCategoriaFinanceira,
  validarComandoCategoriaFinanceira,
  type ComandoCategoriaFinanceira,
} from "./categoria-financeira.ts";
import {
  executarComandoUsuario,
  validarComandoUsuario,
  type ComandoUsuario,
} from "./usuario.ts";
import {
  executarComandoPerfil,
  validarComandoPerfil,
  type ComandoPerfil,
} from "./perfil.ts";

export type { ContextoComando };

export type Comando =
  | { agregado: "lote"; cmd: ComandoLote }
  | { agregado: "devolucao"; cmd: ComandoDevolucao }
  | { agregado: "backlogInicial"; cmd: ComandoBacklogInicial }
  | { agregado: "perda"; cmd: ComandoPerda }
  | { agregado: "fatura"; cmd: ComandoFatura }
  | { agregado: "categoria"; cmd: ComandoCategoria }
  | { agregado: "tipoPeca"; cmd: ComandoTipoPeca }
  | { agregado: "lista"; cmd: ComandoLista }
  | { agregado: "funcionario"; cmd: ComandoFuncionario }
  | { agregado: "insumo"; cmd: ComandoInsumo }
  | { agregado: "config"; cmd: ComandoConfig }
  | { agregado: "preco"; cmd: ComandoPreco }
  | { agregado: "cliente"; cmd: ComandoCliente }
  | { agregado: "usuario"; cmd: ComandoUsuario }
  | { agregado: "perfil"; cmd: ComandoPerfil }
  | { agregado: "categoriaFinanceira"; cmd: ComandoCategoriaFinanceira }
  | { agregado: "conta"; cmd: ComandoConta }
  | { agregado: "responsavel"; cmd: ComandoResponsavel }
  | { agregado: "retirada"; cmd: ComandoRetirada };

/** Os nomes de agregado, derivados da união — nunca escritos à mão. */
export type Agregado = Comando["agregado"];

/**
 * A que Módulo cada agregado pertence, para a Permissão de Módulo.
 *
 * Antes isto não existia em lugar nenhum: `MODULOS` mora em `domain/schema.ts`
 * e os agregados aqui, sem ligação, e a única checagem do sistema era um `if`
 * por agregado dentro de uma rota do Express.
 *
 * `Record<Agregado, Modulo>` não compila com uma chave faltando: agregado novo
 * é obrigado a declarar o Módulo dele.
 */
export const MODULO_DO_AGREGADO: Record<Agregado, Modulo> = {
  lote: "operacional",
  devolucao: "operacional",
  backlogInicial: "operacional",
  perda: "operacional",
  fatura: "financeiro",
  categoria: "configuracao",
  tipoPeca: "configuracao",
  lista: "configuracao",
  funcionario: "configuracao",
  insumo: "configuracao",
  config: "configuracao",
  preco: "configuracao",
  cliente: "configuracao",
  usuario: "configuracao",
  perfil: "configuracao",
  categoriaFinanceira: "financeiro",
  conta: "financeiro",
  responsavel: "financeiro",
  retirada: "financeiro",
};

export function executarComando(
  snapshot: Snapshot,
  comando: Comando,
  ctx: ContextoComando,
): Snapshot {
  switch (comando.agregado) {
    case "lote":
      return executarComandoLote(snapshot, comando.cmd, ctx);
    case "devolucao":
      return executarComandoDevolucao(snapshot, comando.cmd, ctx);
    case "backlogInicial":
      return executarComandoBacklogInicial(snapshot, comando.cmd, ctx);
    case "perda":
      return executarComandoPerda(snapshot, comando.cmd, ctx);
    case "fatura":
      return executarComandoFatura(snapshot, comando.cmd, ctx);
    case "categoria":
      return executarComandoCategoria(snapshot, comando.cmd, ctx);
    case "tipoPeca":
      return executarComandoTipoPeca(snapshot, comando.cmd, ctx);
    case "lista":
      return executarComandoLista(snapshot, comando.cmd, ctx);
    case "funcionario":
      return executarComandoFuncionario(snapshot, comando.cmd, ctx);
    case "insumo":
      return executarComandoInsumo(snapshot, comando.cmd, ctx);
    case "config":
      return executarComandoConfig(snapshot, comando.cmd, ctx);
    case "preco":
      return executarComandoPreco(snapshot, comando.cmd, ctx);
    case "cliente":
      return executarComandoCliente(snapshot, comando.cmd, ctx);
    case "usuario":
      return executarComandoUsuario(snapshot, comando.cmd, ctx);
    case "perfil":
      return executarComandoPerfil(snapshot, comando.cmd, ctx);
    case "categoriaFinanceira":
      return executarComandoCategoriaFinanceira(snapshot, comando.cmd, ctx);
    case "conta":
      return executarComandoConta(snapshot, comando.cmd, ctx);
    case "responsavel":
      return executarComandoResponsavel(snapshot, comando.cmd, ctx);
    case "retirada":
      return executarComandoRetirada(snapshot, comando.cmd, ctx);
    default:
      return assertNunca(comando);
  }
}

/**
 * Um validador por agregado. Era um `switch` sobre `unknown`, e por isso o
 * `default` era um `throw` de string em vez de `assertNunca`: adicionar um
 * agregado à união e esquecer o `case` **compilava**, passava no `npm run check`,
 * funcionava no modo local (o Store chama `executarComando` direto, sem validar)
 * e quebrava só em produção hospedada.
 *
 * `Record<Agregado, …>` não compila com uma chave faltando, e como toda entrada
 * devolve `Comando` — tipo homogêneo — o despacho dispensa cast (ADR-0013).
 */
const VALIDADORES: Record<Agregado, (cmd: unknown) => Comando> = {
  lote: (cmd) => ({ agregado: "lote", cmd: validarComandoLote(cmd) }),
  devolucao: (cmd) => ({ agregado: "devolucao", cmd: validarComandoDevolucao(cmd) }),
  backlogInicial: (cmd) => ({
    agregado: "backlogInicial",
    cmd: validarComandoBacklogInicial(cmd),
  }),
  perda: (cmd) => ({ agregado: "perda", cmd: validarComandoPerda(cmd) }),
  fatura: (cmd) => ({ agregado: "fatura", cmd: validarComandoFatura(cmd) }),
  categoria: (cmd) => ({ agregado: "categoria", cmd: validarComandoCategoria(cmd) }),
  tipoPeca: (cmd) => ({ agregado: "tipoPeca", cmd: validarComandoTipoPeca(cmd) }),
  lista: (cmd) => ({ agregado: "lista", cmd: validarComandoLista(cmd) }),
  funcionario: (cmd) => ({
    agregado: "funcionario",
    cmd: validarComandoFuncionario(cmd),
  }),
  insumo: (cmd) => ({ agregado: "insumo", cmd: validarComandoInsumo(cmd) }),
  config: (cmd) => ({ agregado: "config", cmd: validarComandoConfig(cmd) }),
  preco: (cmd) => ({ agregado: "preco", cmd: validarComandoPreco(cmd) }),
  cliente: (cmd) => ({ agregado: "cliente", cmd: validarComandoCliente(cmd) }),
  usuario: (cmd) => ({ agregado: "usuario", cmd: validarComandoUsuario(cmd) }),
  perfil: (cmd) => ({ agregado: "perfil", cmd: validarComandoPerfil(cmd) }),
  categoriaFinanceira: (cmd) => ({
    agregado: "categoriaFinanceira",
    cmd: validarComandoCategoriaFinanceira(cmd),
  }),
  conta: (cmd) => ({ agregado: "conta", cmd: validarComandoConta(cmd) }),
  responsavel: (cmd) => ({
    agregado: "responsavel",
    cmd: validarComandoResponsavel(cmd),
  }),
  retirada: (cmd) => ({ agregado: "retirada", cmd: validarComandoRetirada(cmd) }),
};

/** Todos os agregados que existem, derivados do mapa. */
export const AGREGADOS = Object.keys(VALIDADORES) as readonly Agregado[];

/** Estreita a chave sem cast no ponto de uso (ADR-0013). */
function ehAgregado(v: unknown): v is Agregado {
  return typeof v === "string" && Object.hasOwn(VALIDADORES, v);
}

/**
 * Transforma um `unknown` (o corpo de um `POST /api/comando`) num `Comando`
 * de verdade, ou lança um erro em português. Só valida a FORMA — as regras
 * de negócio ficam no domínio.
 */
export function validarComando(valor: unknown): Comando {
  const c = comoObjeto(valor, "Comando");
  if (!ehAgregado(c.agregado)) {
    throw new Error(`Agregado de comando desconhecido: ${String(c.agregado)}.`);
  }
  return VALIDADORES[c.agregado](c.cmd);
}
